import { isList, isObjectLike } from './guards';
import type { PropRecord, PropValue } from './types';
import { isSignalLike } from './values';

/** Plain data only: an object literal or a null-prototype object, never a class instance. */
export function isPlainObject<V extends object>(value: V): boolean {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/** True for a value island and region props may carry: JSON, dates, signals, and plain lists and records of those. */
export function isPropValue<T>(value: T): value is T & PropValue {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (value instanceof Date) return true;
  if (isList(value)) return value.every(isPropValue);
  if (!isObjectLike(value)) return false;
  if (isSignalLike(value)) return true;
  return isPlainObject(value) && Object.values(value).every(isPropValue);
}

export function isPropRecord<T extends object>(
  value: T,
): value is T & PropRecord {
  return (
    !isList(value) &&
    !isSignalLike(value) &&
    isPlainObject(value) &&
    Object.values(value).every(isPropValue)
  );
}
